import { GRID_UNIT } from "./constants";
import type { PartCategory, PartShape } from "./types";

/** Shared PNG canvas size per category — art anchors on the same frame within each type. */
export const PART_CATEGORY_CANVAS: Record<
  Extract<PartCategory, "nose" | "fuselage" | "engine" | "fin">,
  { pixelW: number; pixelH: number }
> = {
  nose: { pixelW: 148, pixelH: 269 },
  fuselage: { pixelW: 134, pixelH: 234 },
  engine: { pixelW: 196, pixelH: 227 },
  fin: { pixelW: 92, pixelH: 110 },
};

export interface PartSpriteDef {
  key: string;
  file: string;
  label: string;
  category: Extract<PartCategory, "nose" | "fuselage" | "engine" | "fin">;
  shape: PartShape;
  /** Native art hue (~210° sky blue). Used to compute ROYGBIV shifts. */
  baseHue: number;
}

export interface RoygbivHue {
  id: string;
  name: string;
  /** Degrees passed to ColorMatrixFilter.hue() from the sprite base hue. */
  hueShift: number;
}

/** ROYGBIV palette — one hue-shifted variant per base sprite. */
export const ROYGBIV_HUES: RoygbivHue[] = [
  { id: "red", name: "Red", hueShift: 150 },
  { id: "orange", name: "Orange", hueShift: 180 },
  { id: "yellow", name: "Yellow", hueShift: 210 },
  { id: "green", name: "Green", hueShift: 270 },
  { id: "blue", name: "Blue", hueShift: 0 },
  { id: "indigo", name: "Indigo", hueShift: 30 },
  { id: "violet", name: "Violet", hueShift: 60 },
];

export const PART_SPRITE_DEFS: PartSpriteDef[] = [
  {
    key: "nose_cone",
    file: "/parts/nose_cone.png",
    label: "Cone Nose",
    category: "nose",
    shape: "cone",
    baseHue: 210,
  },
  {
    key: "nose_dome",
    file: "/parts/nose_dome.png",
    label: "Dome Nose",
    category: "nose",
    shape: "dome",
    baseHue: 210,
  },
  {
    key: "nose_window_cone",
    file: "/parts/nose_window_cone.png",
    label: "Window Cone",
    category: "nose",
    shape: "cone",
    baseHue: 210,
  },
  {
    key: "nose_window_dome",
    file: "/parts/nose_window_dome.png",
    label: "Window Dome",
    category: "nose",
    shape: "dome",
    baseHue: 210,
  },
  {
    key: "nose_window_antenna",
    file: "/parts/nose_window_antenna.png",
    label: "Antenna Nose",
    category: "nose",
    shape: "ogive",
    baseHue: 210,
  },
  {
    key: "body_window",
    file: "/parts/body_window.png",
    label: "Window Body",
    category: "fuselage",
    shape: "cylinder",
    baseHue: 210,
  },
  {
    key: "body_triple_window",
    file: "/parts/body_triple_window.png",
    label: "Triple Window Body",
    category: "fuselage",
    shape: "cylinder-panel",
    baseHue: 210,
  },
  {
    key: "body_neon",
    file: "/parts/body_neon.png",
    label: "Neon Body",
    category: "fuselage",
    shape: "cylinder-taper",
    baseHue: 210,
  },
  {
    key: "engine_twin",
    file: "/parts/engine_twin.png",
    label: "Twin Engine",
    category: "engine",
    shape: "engine-dual",
    baseHue: 210,
  },
  {
    key: "engine_triple",
    file: "/parts/engine_triple.png",
    label: "Triple Engine",
    category: "engine",
    shape: "engine",
    baseHue: 210,
  },
  {
    key: "engine_large",
    file: "/parts/engine_large.png",
    label: "Large Engine",
    category: "engine",
    shape: "engine-bell",
    baseHue: 210,
  },
  {
    key: "engine_window_large",
    file: "/parts/engine_window_large.png",
    label: "Window Engine",
    category: "engine",
    shape: "engine-ring",
    baseHue: 210,
  },
  {
    key: "fin_chunky",
    file: "/parts/fin_chunky.png",
    label: "Chunky Fin",
    category: "fin",
    shape: "fin-delta",
    baseHue: 210,
  },
  {
    key: "fin_metal",
    file: "/parts/fin_metal.png",
    label: "Metal Fin",
    category: "fin",
    shape: "fin-swept",
    baseHue: 210,
  },
  {
    key: "fin_stripe",
    file: "/parts/fin_stripe.png",
    label: "Stripe Fin",
    category: "fin",
    shape: "fin-clipped",
    baseHue: 210,
  },
];

export const PART_SPRITE_BY_KEY = Object.fromEntries(
  PART_SPRITE_DEFS.map((def) => [def.key, def]),
) as Record<string, PartSpriteDef>;

/** One blue (native) variant per sprite type — free starters for a fresh save. */
export const STARTER_SPRITE_KEYS = [
  "nose_cone",
  "body_window",
  "engine_twin",
  "fin_chunky",
] as const;

export function spriteGridDimensions(
  category: Extract<PartCategory, "nose" | "fuselage" | "engine" | "fin">,
): { gridW: number; gridH: number } {
  const { pixelW, pixelH } = PART_CATEGORY_CANVAS[category];
  return { gridW: pixelW / GRID_UNIT, gridH: pixelH / GRID_UNIT };
}

export function partIdForSpriteHue(spriteKey: string, hueId: string): string {
  return `${spriteKey}-${hueId}`;
}
